import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Heart, ThumbsUp, MessageSquare, Eye, Calendar, User, Send,
  Image as ImageIcon, Video, Trash2, MoreHorizontal, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import { AuthorProfilePopover } from "../components/AuthorProfilePopover";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_NAMES = { success: "성공스토리", "date-review": "데이트 후기", event: "이벤트" };

export default function CoupleBoardDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [postRes, commRes] = await Promise.all([
          fetch(`${API_URL}/api/couple-posts/${postId}`),
          fetch(`${API_URL}/api/couple-posts/${postId}/comments`),
        ]);
        if (postRes.ok) setPost(await postRes.json());
        if (commRes.ok) setComments(await commRes.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchData();
  }, [postId]);

  const handleLike = async () => {
    if (!isAuthenticated) return alert("로그인이 필요합니다.");
    try {
      const res = await fetch(`${API_URL}/api/couple-posts/${postId}/like`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPost((p) => ({ ...p, likes: data.likes, liked_by: data.liked ? [...(p.liked_by || []), user.id] : (p.liked_by || []).filter((id) => id !== user.id) }));
      }
    } catch { /* ignore */ }
  };

  const handleComment = async () => {
    if (!newComment.trim() || !isAuthenticated) return;
    try {
      const res = await fetch(`${API_URL}/api/couple-posts/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: newComment }),
      });
      if (res.ok) {
        const comment = await res.json();
        setComments((c) => [...c, comment]);
        setNewComment("");
        setPost((p) => ({ ...p, comments: (p.comments || 0) + 1 }));
      }
    } catch { /* ignore */ }
  };

  const handleReply = async (parentId) => {
    if (!replyContent.trim() || !isAuthenticated) return;
    try {
      const res = await fetch(`${API_URL}/api/couple-posts/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: replyContent, parent_id: parentId }),
      });
      if (res.ok) {
        const comment = await res.json();
        setComments((c) => [...c, comment]);
        setReplyTo(null);
        setReplyContent("");
        setPost((p) => ({ ...p, comments: (p.comments || 0) + 1 }));
      }
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      const res = await fetch(`${API_URL}/api/couple-posts/${postId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) navigate("/couple-board");
    } catch { /* ignore */ }
  };

  const getRelativeTime = (d) => {
    if (!d) return "";
    const diff = Math.floor((new Date() - new Date(d)) / 60000);
    if (diff < 1) return "방금 전";
    if (diff < 60) return `${diff}분 전`;
    if (diff < 1440) return `${Math.floor(diff / 60)}시간 전`;
    if (diff < 10080) return `${Math.floor(diff / 1440)}일 전`;
    return new Date(d).toLocaleDateString("ko-KR");
  };

  const isAuthor = user?.id === post?.author_id;
  const isLiked = post?.liked_by?.includes(user?.id);
  const topComments = comments.filter((c) => !c.parent_id);
  const getReplies = (id) => comments.filter((c) => c.parent_id === id);

  if (loading) {
    return <div className="min-h-screen bg-stone-50 pt-20 flex items-center justify-center"><div className="text-stone-400">불러오는 중...</div></div>;
  }
  if (!post) {
    return <div className="min-h-screen bg-stone-50 pt-20 flex items-center justify-center"><div className="text-stone-400">게시글을 찾을 수 없습니다.</div></div>;
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="couple-detail-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Back */}
        <button onClick={() => navigate("/couple-board")} className="flex items-center gap-1 text-stone-500 hover:text-stone-700 mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm">커플 게시판으로</span>
        </button>

        {/* Post */}
        <article className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden mb-6" data-testid="couple-post-article">
          {/* Header */}
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between mb-4">
              <span className="px-3 py-1 bg-burgundy-50 text-burgundy-600 text-xs font-medium rounded-full">
                {CATEGORY_NAMES[post.category] || post.category}
              </span>
              {isAuthor && (
                <button onClick={handleDelete} className="text-stone-400 hover:text-red-500 transition-colors" data-testid="delete-post-btn">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <h1 className="text-2xl font-bold text-stone-800 mb-4" data-testid="couple-post-title">{post.title}</h1>
            <div className="flex items-center gap-4 text-sm text-stone-500">
              <AuthorProfilePopover authorId={post.author_id} authorName={post.author} authorMembership={post.author_membership} position="left">
                <span className="flex items-center gap-1 hover:text-burgundy-600 cursor-pointer" data-testid="couple-post-author">
                  <User className="w-4 h-4" />{post.author}
                </span>
              </AuthorProfilePopover>
              {post.author_membership && post.author_membership !== "basic" && <MembershipBadge membership={post.author_membership} size="md" />}
              <DocumentVerifiedBadge verified={post.author_doc_verified} size="md" />
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{getRelativeTime(post.created_at)}</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="prose max-w-none text-stone-700 leading-relaxed whitespace-pre-wrap" data-testid="couple-post-content">
              {post.content}
            </div>

            {/* Images */}
            {post.images?.length > 0 && (
              <div className="mt-6 grid grid-cols-2 gap-3">
                {post.images.map((img, i) => (
                  <img key={i} src={img.startsWith("http") ? img : `${API_URL}${img}`} alt="" className="rounded-xl w-full object-cover max-h-80" />
                ))}
              </div>
            )}

            {/* Videos */}
            {post.videos?.length > 0 && (
              <div className="mt-6 space-y-3">
                {post.videos.map((vid, i) => (
                  <video key={i} controls className="rounded-xl w-full max-h-96" src={vid.startsWith("http") ? vid : `${API_URL}${vid}`} />
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 border-t border-stone-100 flex items-center gap-6">
            <button onClick={handleLike} className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${isLiked ? "text-burgundy-600" : "text-stone-500 hover:text-burgundy-600"}`} data-testid="like-btn">
              <ThumbsUp className={`w-4 h-4 ${isLiked ? "fill-burgundy-600" : ""}`} />{post.likes || 0}
            </button>
            <span className="flex items-center gap-1.5 text-sm text-stone-500"><MessageSquare className="w-4 h-4" />{post.comments || 0}</span>
            <span className="flex items-center gap-1.5 text-sm text-stone-500"><Eye className="w-4 h-4" />{post.views || 0}</span>
          </div>
        </article>

        {/* Comments */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden" data-testid="couple-comments-section">
          <div className="p-6 border-b border-stone-100">
            <h2 className="font-bold text-stone-800">댓글 {comments.length}개</h2>
          </div>

          {/* Comment input */}
          <div className="p-6 border-b border-stone-100">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-burgundy-100 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-burgundy-500" />
              </div>
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={isAuthenticated ? "댓글을 작성하세요..." : "로그인 후 댓글을 작성할 수 있습니다."}
                  disabled={!isAuthenticated}
                  className="w-full p-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 resize-none"
                  rows={2}
                  data-testid="comment-input"
                />
                <div className="flex justify-end mt-2">
                  <Button onClick={handleComment} disabled={!newComment.trim() || !isAuthenticated} className="bg-burgundy-500 hover:bg-burgundy-600 text-white text-sm" data-testid="comment-submit-btn">
                    <Send className="w-4 h-4 mr-1" />댓글 등록
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Comments list */}
          <div className="divide-y divide-stone-50">
            {topComments.map((comment) => (
              <div key={comment.id} className="p-6" data-testid={`couple-comment-${comment.id}`}>
                <div className="flex gap-3">
                  <AuthorProfilePopover authorId={comment.author_id} authorName={comment.author} authorMembership={comment.author_membership}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-burgundy-300 ${
                      comment.author_membership === "vip" ? "bg-purple-400" :
                      comment.author_membership === "premium" ? "bg-amber-400" :
                      comment.author_membership === "regular" ? "bg-emerald-400" : "bg-slate-400"
                    }`}>
                      <span className="text-white font-bold text-xs">{comment.author?.[0]}</span>
                    </div>
                  </AuthorProfilePopover>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-stone-700 font-medium text-sm">@{comment.author}</span>
                      {comment.author_membership && comment.author_membership !== "basic" && <MembershipBadge membership={comment.author_membership} size="sm" />}
                      <span className="text-stone-400 text-xs">{getRelativeTime(comment.created_at)}</span>
                    </div>
                    <p className="text-stone-600 text-sm leading-relaxed">{comment.content}</p>
                    {isAuthenticated && (
                      <button onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)} className="text-xs text-stone-400 hover:text-burgundy-500 mt-1.5">
                        답글
                      </button>
                    )}

                    {/* Reply input */}
                    {replyTo === comment.id && (
                      <div className="mt-3 flex gap-2">
                        <input
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder={`@${comment.author}님에게 답글...`}
                          className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                          data-testid="reply-input"
                        />
                        <Button onClick={() => handleReply(comment.id)} disabled={!replyContent.trim()} className="bg-burgundy-500 hover:bg-burgundy-600 text-white text-xs px-3" data-testid="reply-submit-btn">
                          등록
                        </Button>
                      </div>
                    )}

                    {/* Replies */}
                    {getReplies(comment.id).map((reply) => (
                      <div key={reply.id} className="mt-3 ml-2 pl-4 border-l-2 border-stone-100" data-testid={`couple-reply-${reply.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-stone-700 font-medium text-xs">@{reply.author}</span>
                          {reply.author_membership && reply.author_membership !== "basic" && <MembershipBadge membership={reply.author_membership} size="sm" />}
                          <span className="text-stone-400 text-xs">{getRelativeTime(reply.created_at)}</span>
                        </div>
                        <p className="text-stone-600 text-xs leading-relaxed">{reply.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {comments.length === 0 && (
              <div className="p-12 text-center text-stone-400 text-sm">아직 댓글이 없습니다. 첫 댓글을 작성해보세요!</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
